import { useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { PlusIcon, Trash2Icon } from "lucide-react";

import { ApiError } from "@/api/client";
import {
  useCreateSource,
  useUpdateSource,
  type ComponentInput,
  type Source,
} from "@/api/sources";
import { ItemCombobox } from "@/components/entity-select/ItemCombobox";
import { LocationCombobox } from "@/components/entity-select/LocationCombobox";
import { SupplierCombobox } from "@/components/entity-select/SupplierCombobox";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { ConsumeOffsetField } from "./ConsumeOffsetField";
import { computeFlavor } from "./sourceFlavor";

interface Props {
  itemId: number;
  /** null = create mode. */
  source: Source | null;
  open: boolean;
  onClose: () => void;
}

interface DraftComponent {
  key: string;
  id?: number;
  item: number | null;
  location: number | null;
  quantity: string;
  consumeOffset: number;
}

let keySeq = 0;
const nextKey = () => `c${keySeq++}`;

export function SourceForm({ itemId, source, open, onClose }: Props) {
  const { t } = useTranslation();
  const isEdit = source != null;

  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [location, setLocation] = useState<number | null>(null);
  const [supplier, setSupplier] = useState<number | null>(null);
  const [priority, setPriority] = useState(0);
  const [leadTime, setLeadTime] = useState(0);
  const [minQty, setMinQty] = useState<string>("");
  const [lotMultiple, setLotMultiple] = useState<string>("");
  const [components, setComponents] = useState<DraftComponent[]>([]);
  const [serverError, setServerError] = useState<string | null>(null);

  const create = useCreateSource(itemId);
  const update = useUpdateSource(itemId);

  useEffect(() => {
    if (!open) return;
    setServerError(null);
    if (source) {
      setCode(source.code);
      setName(source.name);
      setLocation(source.location);
      setSupplier(source.supplier ?? null);
      setPriority(source.priority ?? 0);
      setLeadTime(source.leadTime ?? 0);
      setMinQty(
        source.minimumQuantity != null ? String(source.minimumQuantity) : "",
      );
      setLotMultiple(
        source.lotMultiple != null ? String(source.lotMultiple) : "",
      );
      setComponents(
        (source.components ?? []).map((c) => ({
          key: nextKey(),
          id: c.id,
          item: c.item,
          location: c.location,
          quantity: String(c.quantity),
          consumeOffset: c.consumeOffset ?? 0,
        })),
      );
    } else {
      setCode("");
      setName("");
      setLocation(null);
      setSupplier(null);
      setPriority(0);
      setLeadTime(0);
      setMinQty("");
      setLotMultiple("");
      setComponents([]);
    }
  }, [source, open]);

  // Count ALL draft rows so the badge reflects the presence of a component immediately.
  // -1 sentinel can never equal a real producesItem id so the "transferred" guard won't false-match.
  const flavor = useMemo(
    () =>
      computeFlavor({
        supplier,
        producesItem: itemId,
        location: location ?? -1,
        components: components.map((c) => ({
          item: c.item ?? -1,
          location: c.location ?? -1,
          quantity: Number(c.quantity) || 0,
        })),
      }),
    [supplier, itemId, location, components],
  );

  const isSelfLoop = (c: DraftComponent) =>
    c.item === itemId && c.location === location;

  const isValid =
    code.trim() !== "" &&
    name.trim() !== "" &&
    location != null &&
    leadTime >= 0 &&
    (minQty === "" || Number(minQty) > 0) &&
    (lotMultiple === "" || Number(lotMultiple) >= 1) &&
    components.every(
      (c) =>
        c.item != null &&
        c.location != null &&
        Number(c.quantity) > 0 &&
        !isSelfLoop(c),
    );

  const isPending = create.isPending || update.isPending;

  const addComponent = () =>
    setComponents((prev) => [
      ...prev,
      { key: nextKey(), item: null, location, quantity: "1", consumeOffset: 0 },
    ]);

  const patchComponent = (key: string, patch: Partial<DraftComponent>) =>
    setComponents((prev) =>
      prev.map((c) => (c.key === key ? { ...c, ...patch } : c)),
    );

  const removeComponent = (key: string) =>
    setComponents((prev) => prev.filter((c) => c.key !== key));

  const onError = (err: unknown) => {
    setServerError(
      (err instanceof ApiError && err.error?.message) ||
        t("sources.form.saveError"),
    );
  };

  const handleSave = () => {
    setServerError(null);
    const componentPayload: ComponentInput[] = components.map((c) => ({
      ...(c.id != null ? { id: c.id } : {}),
      item: c.item as number,
      location: c.location as number,
      quantity: c.quantity,
      consumeOffset: c.consumeOffset,
    }));
    const payload = {
      code: code.trim(),
      name: name.trim(),
      location: location as number,
      supplier,
      priority,
      leadTime,
      minimumQuantity: minQty === "" ? null : minQty,
      lotMultiple: lotMultiple === "" ? null : Number(lotMultiple),
      components: componentPayload,
    };
    if (isEdit) {
      update.mutate(
        { id: source!.id, ...payload },
        { onSuccess: onClose, onError },
      );
    } else {
      create.mutate(payload, { onSuccess: onClose, onError });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isEdit ? t("sources.form.editTitle") : t("sources.form.newTitle")}
            <Badge variant="secondary">{t(`sources.flavor.${flavor}`)}</Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="flex max-h-[70vh] flex-col gap-4 overflow-y-auto pr-1">
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("sources.form.code")}>
              <Input value={code} onChange={(e) => setCode(e.target.value)} />
            </Field>
            <Field label={t("sources.form.name")}>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
            <Field label={t("sources.form.location")}>
              <LocationCombobox value={location} onChange={setLocation} />
            </Field>
            <Field label={t("sources.form.supplier")}>
              <SupplierCombobox
                value={supplier}
                onChange={setSupplier}
                allowClear
              />
            </Field>
            <Field label={t("sources.form.leadTime")}>
              <Input
                type="number"
                min={0}
                value={leadTime}
                onChange={(e) =>
                  setLeadTime(Math.max(0, Number(e.target.value) || 0))
                }
              />
            </Field>
            <Field label={t("sources.form.priority")}>
              <Input
                type="number"
                value={priority}
                onChange={(e) => setPriority(Number(e.target.value) || 0)}
              />
            </Field>
            <Field label={t("sources.form.minQty")}>
              <Input
                type="number"
                min={0}
                value={minQty}
                onChange={(e) => setMinQty(e.target.value)}
              />
            </Field>
            <Field label={t("sources.form.lotMultiple")}>
              <Input
                type="number"
                min={1}
                value={lotMultiple}
                onChange={(e) => setLotMultiple(e.target.value)}
              />
            </Field>
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <Label>{t("sources.form.componentsHeading")}</Label>
              <Button variant="outline" size="sm" onClick={addComponent}>
                <PlusIcon className="mr-1 size-4" />
                {t("sources.form.addComponent")}
              </Button>
            </div>
            {components.map((c) => (
              <div
                key={c.key}
                className="bg-muted/20 flex flex-col gap-2 rounded-md border p-3"
              >
                <div className="grid grid-cols-[1fr_1fr_auto] items-end gap-2">
                  <Field label={t("sources.form.componentItem")}>
                    <ItemCombobox
                      value={c.item}
                      onChange={(v) => patchComponent(c.key, { item: v })}
                    />
                  </Field>
                  <Field label={t("sources.form.componentLocation")}>
                    <LocationCombobox
                      value={c.location}
                      onChange={(v) => patchComponent(c.key, { location: v })}
                    />
                  </Field>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={t("common.delete")}
                    onClick={() => removeComponent(c.key)}
                  >
                    <Trash2Icon className="text-muted-foreground size-4" />
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Field label={t("sources.form.componentQty")}>
                    <Input
                      type="number"
                      min={0}
                      value={c.quantity}
                      onChange={(e) =>
                        patchComponent(c.key, { quantity: e.target.value })
                      }
                    />
                  </Field>
                  <ConsumeOffsetField
                    id={`offset-${c.key}`}
                    value={c.consumeOffset}
                    onChange={(d) =>
                      patchComponent(c.key, { consumeOffset: d })
                    }
                    leadTimeDays={leadTime}
                  />
                </div>
                {isSelfLoop(c) && (
                  <p className="text-destructive text-xs">
                    {t("sources.form.selfLoopError")}
                  </p>
                )}
              </div>
            ))}
          </div>

          {serverError && (
            <p className="text-destructive text-sm">{serverError}</p>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t("common.buttons.cancel")}
          </Button>
          <Button onClick={handleSave} disabled={!isValid || isPending}>
            {isEdit ? t("common.buttons.save") : t("common.buttons.create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}
